// Self-contained Class Audit CSV builder. No third-party dependency (deliberately NOT the
// react-export-json-csv the Season Audit grid uses, which exports the whole dataset). This
// serializes exactly the rows it is handed -- the caller passes the page's `displayedRows`, so the
// export always matches the current UI view. Derived columns (Status and the issue booleans) use
// the SAME getClassStatus the grid uses, so the file can never disagree with the screen.

import { getClassStatus } from './classQualityChecks';

// Human-readable Tracking Season label from the numeric code; blank when unavailable.
const TRACKING_SEASON_LABEL = { 1: 'Spring', 2: 'Summer', 3: 'Fall', 4: 'Winter' };
function trackingSeasonLabel(v) {
  return TRACKING_SEASON_LABEL[v] || '';
}

// Booleans export as literal "true"/"false" so the file filters cleanly outside the app.
function boolText(v) {
  return v === true || v === 1 ? 'true' : 'false';
}

// Column definitions, in the approved export order. Each: { header, value(row) }.
// value() returns a raw JS value (string/number/null); escaping happens later in toCsvField.
export const CSV_COLUMNS = [
  { header: 'ComCode',                   value: (r) => r.com_code },
  // Friendly franchise name, falling back to com_code (same rule as the grid).
  { header: 'Franchise',                 value: (r) => r.franchise_name || r.com_code },
  { header: 'Facility',                  value: (r) => r.facility_name },
  { header: 'Tracking Year',             value: (r) => r.trackingyear },
  { header: 'Tracking Season',           value: (r) => trackingSeasonLabel(r.trackingseason) },
  { header: 'Class Name',                value: (r) => r.class_name },
  // Effective / live-resolved level (blank when unassigned).
  { header: 'Canonical Level',           value: (r) => r.effective_level_name },
  // Raw, stable mapping_source for reliable external filtering.
  { header: 'Level Mapping Source',      value: (r) => r.mapping_source },
  { header: 'Suggested Level',           value: (r) => r.suggested_canonical_level_name },
  { header: 'Matched Pattern',           value: (r) => r.team_name_pattern },
  // Raw warehouse flag: equally-ranked rules disagreed on the level for this class. Stays true even
  // once a manual value settles it -- the Status column carries the derived loud/quiet distinction
  // (Ambiguous mapping vs Manual override), so one raw column plus Status covers both readings.
  { header: 'Ambiguous Rule Match',      value: (r) => boolText(r.is_ambiguous_match) },
  { header: 'Instructor Count',          value: (r) => r.instructor_count ?? 0 },
  // FULL instructor list (not the grid's capped "3 names + N more").
  { header: 'Instructors',               value: (r) => r.instructor_names },
  { header: 'Status',                    value: (r) => getClassStatus(r).label },
  { header: 'Missing Level',             value: (r) => boolText(getClassStatus(r).levelMissing) },
  { header: 'Instructor Not Tracked',    value: (r) => boolText(getClassStatus(r).instructorNotTracked) },
  { header: 'Warehouse Refresh Pending', value: (r) => boolText(r.warehouse_refresh_pending) },
  { header: 'Team Dim ID',               value: (r) => r.team_dim_id },
  { header: 'Season ID',                 value: (r) => r.season_id },
];

// RFC 4180 field escaping: null/undefined -> empty; a field containing a comma, double-quote, or
// CR/LF is wrapped in double-quotes with internal quotes doubled.
export function toCsvField(value) {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

// Serialize rows to a CSV string (header row + one row per class, in the given order).
// CRLF line breaks (RFC 4180 / Excel-friendly).
export function toCsv(rows, columns = CSV_COLUMNS) {
  const headerLine = columns.map((c) => toCsvField(c.header)).join(',');
  const dataLines = (rows || []).map((row) =>
    columns.map((c) => toCsvField(c.value(row))).join(',')
  );
  return [headerLine, ...dataLines].join('\r\n');
}

// Slugify a scope token for the filename (lowercase, non-alphanumerics -> single hyphen, trimmed).
function slug(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Scope-aware filename: class-audit-<year>[-<franchise>]-<quickview>.csv
// franchisePart is omitted when no franchise is selected.
export function buildFilename({ trackingYear, franchiseLabel, quickViewLabel }) {
  const parts = ['class-audit', slug(trackingYear)];
  const fr = slug(franchiseLabel);
  if (fr) parts.push(fr);
  const qv = slug(quickViewLabel);
  if (qv) parts.push(qv);
  return `${parts.filter(Boolean).join('-')}.csv`;
}

// Build the CSV and trigger a browser download. Prepends a UTF-8 BOM so Excel detects UTF-8.
export function downloadClassAuditCsv(rows, filename, columns = CSV_COLUMNS) {
  const csv = toCsv(rows, columns);
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
